import React, { useState, useEffect, useCallback } from "react";
import { Calendar, momentLocalizer, Views } from "react-big-calendar";
import moment from "moment-timezone";
import axios from "axios";
import DatePicker from "react-datepicker";
import { useSearchParams } from "react-router-dom";
import StaffSessionModal from "./StaffSessionModal";
import StaffLayout from "./staff/StaffLayout";
import { useFocusedPoll } from "../hooks/useFocusedPoll";
import { hasAnyAddon } from "../utils/addonIndicatorUtils";
import "react-datepicker/dist/react-datepicker.css";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "./SuiteCalendarDashboard.css";

const localizer = momentLocalizer(moment);

// We'll fetch operating hours from the backend API

const resources = [
  { resourceId: 1, resourceTitle: "Massage #1" },
  { resourceId: 2, resourceTitle: "Sauna #2" },
  { resourceId: 3, resourceTitle: "Sauna #3" },
  { resourceId: 4, resourceTitle: "Sauna #4" },
  { resourceId: 5, resourceTitle: "Sauna #5" },
  { resourceId: 6, resourceTitle: "Sauna #6" },
  { resourceId: 7, resourceTitle: "Sauna #7" },
  { resourceId: 8, resourceTitle: "Sauna #8" },
  { resourceId: 9, resourceTitle: "Red Light #9" },
  { resourceId: 10, resourceTitle: "Red Light #10" },
];

function SuiteCalendarDashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedDate, setSelectedDate] = useState(() => {
    const dateParam = searchParams.get("date");
    if (dateParam) {
      const parsed = moment(dateParam, "YYYY-MM-DD", true);
      if (parsed.isValid()) {
        return parsed.toDate();
      }
    }

    return new Date();
  });
  const [events, setEvents] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [operatingHours, setOperatingHours] = useState({
    startHour: 10,
    endHour: 19,
    isOpen: true,
  });
  const localToken = localStorage.getItem("jwtToken");

  useEffect(() => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("date", moment(selectedDate).format("YYYY-MM-DD"));

    if (nextParams.toString() !== searchParams.toString()) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [searchParams, selectedDate, setSearchParams]);

  // Fetch operating hours for the selected date
  useEffect(() => {
    async function fetchOperatingHours() {
      try {
        const dayOfWeek = moment(selectedDate).day(); // 0 = Sunday, 6 = Saturday
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/operating-hours/${dayOfWeek}`,
          { headers: { Authorization: `Bearer ${localToken}` } }
        );

        if (response.data.operatingHours) {
          const { openTime, closeTime, isOpen } = response.data.operatingHours;
          if (isOpen) {
            const [startHour] = openTime.split(":").map(Number);
            const [endHour] = closeTime.split(":").map(Number);
            setOperatingHours({ startHour, endHour, isOpen });
          } else {
            setOperatingHours({ startHour: 10, endHour: 19, isOpen: false });
          }
        }
      } catch (error) {
        console.error("Error fetching operating hours:", error);
        // Use default hours if API call fails
        setOperatingHours({ startHour: 10, endHour: 19, isOpen: true });
      }
    }

    fetchOperatingHours();
  }, [selectedDate, localToken]);

  // Fetch bookings for the selected date. Extracted to a memoized
  // callback so the Tier 1 focus/visibility/poll hook below can call
  // it independently of the mount/selectedDate effect.
  const fetchBookings = useCallback(async () => {
    try {
      const formattedDate = moment(selectedDate).format("YYYY-MM-DD");

      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/suites/all-suites-bookings?date=${formattedDate}`,
        { headers: { Authorization: `Bearer ${localToken}` } }
      );

      // Map bookings to events for react-big-calendar.
      const newEvents = response.data.bookings.map((booking) => {
        // STAFF-ADDON-VISIBILITY-001 — compact add-on marker.
        // Graceful degradation: BE projection may not include these
        // fields yet (Simone's PR firing in parallel); hasAnyAddon
        // returns false in that case so the marker simply does not
        // render until BE deploys.
        const addonMarker = hasAnyAddon(booking) ? "* " : "";
        return {
          title: `${addonMarker}${booking.fullName || "Unknown User"}`,
          start: new Date(booking.appointmentDate),
          end: moment(booking.appointmentDate)
            .add(booking.duration, "minutes")
            .toDate(),
          resourceId: booking.suiteAssignment.number,
          bookingId: booking._id,
          userId: booking.user || booking.userId || null,
          hasAddon: hasAnyAddon(booking),
        };
      });

      setEvents(newEvents);
    } catch (error) {
      console.error("Error fetching suite bookings:", error);
    }
  }, [selectedDate, localToken]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  // Tier 1 sync posture: 30s visibility-aware poll + refetch on tab
  // focus / window focus / network reconnect. Stale calendar grid is
  // the #1 multi-tab staff complaint.
  useFocusedPoll(fetchBookings, 30000, [fetchBookings]);

  // Use operating hours from state
  const { startHour, endHour, isOpen } = operatingHours;
  const minTime = new Date(
    selectedDate.getFullYear(),
    selectedDate.getMonth(),
    selectedDate.getDate(),
    startHour,
    0,
    0
  );
  const maxTime = new Date(
    selectedDate.getFullYear(),
    selectedDate.getMonth(),
    selectedDate.getDate(),
    endHour,
    0,
    0
  );

  // Handle event selection (when a session is clicked)
  const handleSelectEvent = async (event) => {
    try {
      // Fetch the booking details using the booking ID
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/staff/bookings/${event.bookingId}`,
        { headers: { Authorization: `Bearer ${localToken}` } }
      );

      if (response.data.booking) {
        setSelectedBooking(response.data.booking);
        setShowModal(true);
      } else {
        console.error("No booking found with ID:", event.bookingId);
      }
    } catch (error) {
      console.error("Error fetching booking details:", error);
    }
  };

  // Handle closing the modal
  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedBooking(null);
  };

  return (
    <StaffLayout>
      <div className="suite-calendar-dashboard">
        <div style={{ padding: "20px" }}>
          <h2 style={{ textAlign: "center" }}>All Suite Calendars</h2>
          <div style={{ textAlign: "center", marginBottom: "10px" }}>
            <DatePicker
              selected={selectedDate}
              onChange={(date) => setSelectedDate(date)}
              dateFormat="MMMM d, YYYY"
            />
          </div>
          <Calendar
            localizer={localizer}
            events={events}
            resources={resources}
            resourceIdAccessor="resourceId"
            resourceTitleAccessor="resourceTitle"
            views={{ day: true }}
            defaultView={Views.DAY}
            date={selectedDate}
            onNavigate={(newDate) => setSelectedDate(newDate)}
            min={minTime} // Set the minimum time
            max={maxTime} // Set the maximum time
            components={{ toolbar: () => null }}
            style={{ height: 800 }}
            onSelectEvent={handleSelectEvent} // Add event selection handler
          />
        </div>

        {/* Render the modal when a booking is selected */}
        {showModal && selectedBooking && (
          <StaffSessionModal
            booking={selectedBooking}
            onClose={handleCloseModal}
          />
        )}
      </div>
    </StaffLayout>
  );
}

export default SuiteCalendarDashboard;
